import { Component, EventEmitter, Inject, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ActionNotification, ActionTrigger } from '@core/models/action-item-templates/action-item-details.model';
import { 
  PostNotificationTrigger, 
  AddEditActionItemNotificationDialog,
  AddEditActionItemNotificationForm,
  PostActionNotification,
  TriggerGroupForm,
  TriggerOption } from '@core/models/action-item-templates/add-edit-action-item-notification.model';
import { ErrorService, LayoutService } from '@core/services';
import { ActionItemDetailsStoreService } from '@core/services/action-item-details-store.service';
import { ActionItemsTemplatesService } from '@core/services/action-item-templates.service';
import { indicate, noChangesReplacer, closeDialogOnEscape } from '@shared/helpers';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { StiiraError } from '@core/models';
import { ActionNotificationTiggerForms } from '@core/enums/action-notification-trigger-forms.enum';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';

@Component({
    selector: 'app-add-edit-action-item-notification',
    templateUrl: './add-edit-action-item-notification.component.html',
    styleUrl: './add-edit-action-item-notification.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddEditActionItemNotificationComponent {
  static readonly componentName = 'AddEditActionItemNotificationComponent';
  
  @Output() isEditing = new EventEmitter<boolean>();

  public form: FormGroup<AddEditActionItemNotificationForm>;
  public formInitValues: any;
  public isSubmitting$: Subject<boolean>;
  public isMobile$: Observable<boolean>;
  public warningTriggersOpenState: boolean = true;
  public dueTriggersOpenState: boolean = true;
  public availableWarningOptions: TriggerOption[];
  public availableDueOptions: TriggerOption[];
  public actionNotificationTriggerForms = ActionNotificationTiggerForms;
  public position = position;
  public onDragEnded = onDragEnded;

  private formChangeEmitted: boolean = false;
  private destroy$: Subject<void> = new Subject<void>();

  get noChanges(): boolean {
    return JSON.stringify(this.form.value, noChangesReplacer) === JSON.stringify(this.formInitValues, noChangesReplacer);
  }

  get warningTriggers(): FormArray<FormGroup<TriggerGroupForm>> {
    return this.form.controls.warningTriggers as FormArray<FormGroup<TriggerGroupForm>>;
  }

  get dueTriggers(): FormArray<FormGroup<TriggerGroupForm>> {
    return this.form.controls.dueTriggers as FormArray<FormGroup<TriggerGroupForm>>;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      actionItemId: number;
      notificationDetails?: ActionNotification;
      options: AddEditActionItemNotificationDialog;
      sysText: any;
      unsavedChangesSysText: any
    },
    private dialogRef: MatDialogRef<AddEditActionItemNotificationComponent>,
    private service: ActionItemsTemplatesService,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private store: ActionItemDetailsStoreService,
    private errorService: ErrorService,
    private layoutService: LayoutService
    ) { 
    this.isSubmitting$ = new Subject();
    this.form = this.fb.group({
      notificationName: this.fb.control(null, Validators.required),
      description: this.fb.control(null),
      warningTriggers: this.fb.array([]),
      dueTriggers: this.fb.array([])
    })
  }

  ngOnInit(): void {
    closeDialogOnEscape(this.dialogRef, this.isSubmitting$, () => this.cancel());

    this.isMobile$ = this.layoutService.isMobile$();
    this.availableWarningOptions = this.data.options.warningTriggerOptions;
    this.availableDueOptions = this.data.options.dueTriggerOptions;
    this.setupForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public cancel(): void {
    if (this.noChanges) {
      this.dialogRef.close();
    } else {
      this.openUnsavedChangesDialog();
    }
  }

  public save(): void {
    this.form.markAsUntouched();
    let warningTriggers: PostNotificationTrigger[] = [];
    this.warningTriggers.value.forEach((wt, index) => {
      if (wt.trigger) {
        warningTriggers.push({
          index: index,
          trigger: wt.trigger,
          businessDaysBefore: wt.businessDaysBefore,
          businessDaysAfter: wt.businessDaysAfter,
          calendarDaysBefore: wt.calendarDaysBefore,
          calendarDaysAfter: wt.calendarDaysAfter,
          caseTimespanGreaterThanDays: wt.caseTimespanGreaterThanDays,
          caseTimespanLessThanDays: wt.caseTimespanLessThanDays,
          caseTimespanMidpoint: wt.caseTimespanMidpoint,
          otherActionItem: wt.otherActionItem
        });
      }
    });
    
    let dueTriggers: PostNotificationTrigger[] = [];
    this.dueTriggers.value.forEach((dt, index) => {
      if (dt.trigger) {
        dueTriggers.push({
          index: index,
          trigger: dt.trigger,
          businessDaysBefore: dt.businessDaysBefore,
          businessDaysAfter: dt.businessDaysAfter,
          calendarDaysBefore: dt.calendarDaysBefore,
          calendarDaysAfter: dt.calendarDaysAfter,
          caseTimespanGreaterThanDays: dt.caseTimespanGreaterThanDays,
          caseTimespanLessThanDays: dt.caseTimespanLessThanDays,
          caseTimespanMidpoint: dt.caseTimespanMidpoint,
          otherActionItem: dt.otherActionItem
        });
      }
    });

    const dto: PostActionNotification = {
      actionItemId: this.data.actionItemId,
      notificationId: this.data.notificationDetails?.id,
      notificationName: this.form.controls.notificationName.value,
      description: this.form.controls.description.value,
      warningTriggers: warningTriggers,
      dueTriggers: dueTriggers
    }

    if (this.data.notificationDetails) {
      dto.notificationId = this.data.notificationDetails.id;
      this.service.postEditActionItemNotification(dto)
        .pipe(indicate(this.isSubmitting$))
        .subscribe((res) => {
          this.store.actionItemDetails = res;
          this.dialogRef.close(res);
        }, (err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors));
    } else {
      this.service.postNewActionItemNotification(dto)
        .pipe(indicate(this.isSubmitting$))
        .subscribe((res) => {
          this.store.actionItemDetails = res;
          this.dialogRef.close(res);
        }, (err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors));
    }
  }

  public addTriggerFormGroup(input: {formArray: FormArray<FormGroup<TriggerGroupForm>>, options: TriggerOption[], data?: ActionTrigger}) {
    const triggerOption = input.data ? input.options.find(to => to.id == input.data.id) : null;

    const triggerGroup = this.fb.group<TriggerGroupForm>({
      trigger: this.fb.control(input.data?.id ?? null),
      showBusinessDaysBefore: this.fb.control(triggerOption?.showBusinessDaysBefore ?? false),
      businessDaysBefore: this.fb.control(input.data?.businessDaysBefore ?? null),

      showBusinessDaysAfter: this.fb.control(triggerOption?.showBusinessDaysAfter ?? false),
      businessDaysAfter: this.fb.control(input.data?.businessDaysAfter ?? null),

      showCalendarDaysBefore: this.fb.control(triggerOption?.showCalendarDaysBefore ?? false),
      calendarDaysBefore: this.fb.control(input.data?.calendarDaysBefore ?? null),

      showCalendarDaysAfter: this.fb.control(triggerOption?.showCalendarDaysAfter ?? false),
      calendarDaysAfter: this.fb.control(input.data?.calendarDaysAfter ?? null),

      showCaseTimespanGreaterThanDays: this.fb.control(triggerOption?.showCaseTimespanGreaterThanDays ?? false),
      caseTimespanGreaterThanDays: this.fb.control(input.data?.caseTimespanGreaterThanDays ?? null),

      showCaseTimespanLessThanDays: this.fb.control(triggerOption?.showCaseTimespanLessThanDays ?? false),
      caseTimespanLessThanDays: this.fb.control(input.data?.caseTimespanLessThanDays ?? null),

      showCaseTimespanMidpoint: this.fb.control(triggerOption?.showCaseTimespanMidpoint ?? false),
      caseTimespanMidpoint: this.fb.control(input.data?.caseTimespanMidpoint ?? null),

      showOtherActionItem: this.fb.control(triggerOption?.showOtherActionItem ?? false),
      otherActionItem: this.fb.control(input.data?.otherActionItem?.id as number ?? null)
    });

    triggerGroup.controls.trigger.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        triggerGroup.patchValue({
          businessDaysBefore: null,
          businessDaysAfter: null,
          calendarDaysBefore: null,
          calendarDaysAfter: null,
          caseTimespanGreaterThanDays: null,
          caseTimespanLessThanDays: null,
          caseTimespanMidpoint: null,
          otherActionItem: null
        });

        const selectedOption =  input.options.find(to => to.id == res);
        if (selectedOption) {
          triggerGroup.patchValue({
            showBusinessDaysBefore: selectedOption.showBusinessDaysBefore,
            showBusinessDaysAfter: selectedOption.showBusinessDaysAfter,
            showCalendarDaysBefore: selectedOption.showCalendarDaysBefore,
            showCalendarDaysAfter: selectedOption.showCalendarDaysAfter,
            showCaseTimespanGreaterThanDays: selectedOption.showCaseTimespanLessThanDays,
            showCaseTimespanLessThanDays: selectedOption.showCaseTimespanLessThanDays,
            showCaseTimespanMidpoint: selectedOption.showCaseTimespanMidpoint,
            showOtherActionItem: selectedOption.showOtherActionItem
          });
        } else {
          triggerGroup.patchValue({
            showBusinessDaysBefore: false,
            showBusinessDaysAfter: false,
            showCalendarDaysBefore: false,
            showCalendarDaysAfter: false,
            showCaseTimespanGreaterThanDays: false,
            showCaseTimespanLessThanDays: false,
            showCaseTimespanMidpoint: false,
            showOtherActionItem: false
          });
        }
      });
  
    input.formArray.push(triggerGroup);
  }

  private setupForm(): void {
    if (this.data.notificationDetails) {
      this.form.patchValue({
        notificationName: this.data.notificationDetails.name,
        description: this.data.notificationDetails.description
      });

      if (this.data.notificationDetails.warningTriggers.length > 0) {
        this.data.notificationDetails.warningTriggers.forEach((triggerData) => {
          this.addTriggerFormGroup({formArray: this.warningTriggers, options: this.data.options.warningTriggerOptions, data: triggerData});
        });
      } else {
        this.addTriggerFormGroup({formArray: this.warningTriggers, options: this.data.options.warningTriggerOptions});
      }

      if (this.data.notificationDetails.dueTriggers.length > 0) {
        this.data.notificationDetails.dueTriggers.forEach((triggerData) => {
          this.addTriggerFormGroup({formArray: this.dueTriggers, options: this.data.options.warningTriggerOptions, data: triggerData});
        });
      } else {
        this.addTriggerFormGroup({formArray: this.dueTriggers, options: this.data.options.dueTriggerOptions});
      }
    } else {
      this.addTriggerFormGroup({formArray: this.warningTriggers, options: this.data.options.warningTriggerOptions});
      this.addTriggerFormGroup({formArray: this.dueTriggers, options: this.data.options.dueTriggerOptions});
    }

    this.warningTriggers.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.availableWarningOptions = this.updateTriggerOptions(this.warningTriggers, this.data.options.warningTriggerOptions);
      });

    this.dueTriggers.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.availableDueOptions = this.updateTriggerOptions(this.dueTriggers, this.data.options.dueTriggerOptions);
      });

    this.formInitValues = { ...this.form.value };
    this.setIsEditing();
  }

  private updateTriggerOptions(formArray: FormArray<FormGroup<TriggerGroupForm>>, options: TriggerOption[]): TriggerOption[] {
    let selectedOptions: number[] = [];
    formArray.controls.forEach((formGroup) => {
      if (formGroup.controls.trigger.value) {
        selectedOptions.push(formGroup.controls.trigger.value);
      }
    })
    return options.filter(option => !selectedOptions.includes(option.id as number));
  }

  private setIsEditing() {
    setTimeout(() => {
      this.form.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (!this.formChangeEmitted && !this.noChanges) {
            this.isEditing.emit(true);
            this.formChangeEmitted = true;
          } else if (this.noChanges) {
            this.isEditing.emit(false);
            this.formChangeEmitted = false;
          }
        });
    }, 0);
  }

  private openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.data.unsavedChangesSysText
    };
    
    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.dialogRef.close();
        }
      });
  }
}
